import { useEffect, useState, useRef } from "react";
import { useRouter } from "next/router";
import { toast } from "react-hot-toast";
import cls from "classnames";
import Turnstile from "react-turnstile";

import FieldWrapper from "./FieldWrapper";
import PhoneInput from "./PhoneInputField";
import GetOtpBtn from "./GetOtpBtn";
import ResendOTP from "./ResendOTP";
import Spinner from "@qoohoo/common/library/loader/Spinner";
import countries from "@qoohoo/common/constants/country/countries";

import { NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY, QOOHOO_WEBSITE_DOMAIN } from "constants/env";
import httpf from "utils/api/frontend/httpf";
import httpf_noauth from "utils/api/frontend/httpf_noauth";
import useSessionStore from "store/sessionStore";
import routes from "routes";
import getAuthMe from "helpers/getAuthMe";
import getIpInfo from "./api/get-ipinfo";


const INITIAL_SELECTED_COUNTRY = {
  phone_code: "91",
  code: "IN",
  name: "India",
};

export default function AuthenticationField({
  label,
  type,
  name,
  placeholder,
  onChange,
  disabled,
  formatValue,
  validator,
  required,
  hint,
  value,
  options,
  visible,
  className,
  formData,
}) {
  const router = useRouter();
  const setUserData = useSessionStore((state) => state.setUserData);

  const [isOTPScreen, setIsOTPScreen] = useState(false);
  const [isLoadingSMS, setIsLoadingSMS] = useState(false);
  const [isLoadingWA, setIsLoadingWA] = useState(false);

  const [userIpInfo, setUserIpInfo] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(INITIAL_SELECTED_COUNTRY);
  const [phoneNumber, _setPhoneNumber] = useState("");
  const [otp, setOTP] = useState("");
  const [error, setError] = useState("");

  
  const [shouldSendInWhatsapp, setShouldSendInWhatsapp] = useState(false);
  
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  const selectedCountryCode = selectedCountry.phone_code;

  const saveTurnstileToken = (token) => {
    setTurnstileToken(token);
  }

  const onTurnstileError = () => {
    setTurnstileToken("dummy-token");
  }

  function resetTurnstile () {
    setTurnstileToken("");
    window?.turnstile?.reset(turnstileRef.current);
  }

  // function to handle get otp action
  function handleGetOtp(e) {
    e.preventDefault();
    setIsLoading(true);

    sendOtp(
      phoneNumber,
      selectedCountryCode,
      shouldSendInWhatsapp,
      () => {
        setIsOTPScreen(true);
        setIsLoading(false);
      },
      () => {
        setIsOTPScreen(false);
        setIsLoading(false);
      }
    );
  }

  useEffect(() => {
    if (otp.length === 6) {
      verifyOtp();
    }
  }, [otp]);

  // function to call api to send otp
  async function sendOtp(
    phoneNumber,
    countryCode,
    sendInWhatsapp,
    onSuccess,
    onError
  ) {
    if (phoneNumber.length <= 4 || !countryCode) {
      toast.error("Enter correct phone number");
      return;
    }

    httpf_noauth
      .post(`${QOOHOO_WEBSITE_DOMAIN}/api/auth/mobile/request`, {
        mobile: phoneNumber,
        countryCode: countryCode,
        sendInWhatsapp: sendInWhatsapp,
        turnstileToken: turnstileToken,
      })
      .then(() => {
        toast.success("OTP sent successfully.");

        if (typeof onSuccess === "function") {
          onSuccess();
        }
      })
      .catch((err) => {
        console.error(err);
        // Sentry.captureException(err);

        const errors =
        err?.response?.data?.data?.message ||
        err?.response?.data?.message ||
        err?.response?.data?.data?.non_field_errors ||
        err?.response?.data?.non_field_errors ||
        err?.response?.data?.data;

        if (errors && (!(errors instanceof Array) || errors.length > 0)) {
          toast.error(errors);
        } else {
          toast.error("Error sending OTP");
        }

        onError && onError();
      }).finally(() => {
        resetTurnstile();
      });

  }

  function resendOtp(shouldSendInWhatsapp) {
    setIsLoading(true);
    setOTP("");

    sendOtp(phoneNumber, selectedCountryCode, shouldSendInWhatsapp).finally(
      () => {
        setIsLoading(false);
      }
    );
  }

  function setPhoneNumber(inputValue = "") {
    let phoneNumber = Number(inputValue);

    if (!Number.isInteger(phoneNumber)) {
      return;
    } else if (inputValue === "") {
      _setPhoneNumber("");
    } else {
      _setPhoneNumber(inputValue);
    }
  }

  function setIsLoading(isLoading) {
    if (shouldSendInWhatsapp) {
      setIsLoadingWA(isLoading);
    } else {
      setIsLoadingSMS(isLoading);
    }
  }

  function handlePhoneNumberChange(e) {
    let phoneNumber = e.target?.value;
    const isCountryCodeOfIndia = selectedCountryCode === "91";

    if (isCountryCodeOfIndia) {
      if (phoneNumber.length <= 10) {
        setPhoneNumber(phoneNumber);
      }
    } else {
      if (phoneNumber.length <= 14) {
        setPhoneNumber(phoneNumber);
      }
    }

    if (phoneNumber.length > 4) {
      setError("");
    }
  }

  function handlePhoneNumberBlur() {
    if (phoneNumber.length > 0 && phoneNumber.length <= 4) {
      setError("Enter correct phone number");
    } else {
      setError("");
    }
  }

  function handleOtpChange(e) {
    e.preventDefault();
    let otpInputValue = e.target.value;

    if (otpInputValue.length <= 6) {
      setOTP(otpInputValue);
    }
  }

  function verifyOtp(e) {
    e?.preventDefault();

    setIsLoading(true);

    if (otp.length < 6) {
      toast.error("Please enter 6 digit OTP");
      setIsLoading(false);
      return;
    }

    const containsOtherContentMode = formData?.user_content_mode?.some(
      (item) => item.value === "others"
    );

    const contentModes = formData?.user_content_mode?.map((item) => item.value);
    const contentModsWithoutOthers = contentModes?.filter(
      (item) => item !== "others"
    );

    const payload = {
      mobile: phoneNumber,
      countryCode: selectedCountryCode,
      otp: otp,
      onboarding_form: {
        name: formData?.user_fullname,
        link: formData?.user_social_link,
        content_mode: [],
        creator_categories: [formData?.user_category?.uid],
        country: formData?.user_country?.code,
        // community_size: formData?.user_community_size,
      },
    };

    if (containsOtherContentMode)
      payload.onboarding_form["content_mode_other"] =
        formData?.user_content_mode_other;

    let isErrRes = null;

    httpf_noauth
      .post(`${QOOHOO_WEBSITE_DOMAIN}/api/auth/mobile/validate`, payload)
      .then(async () => {
        return getAuthMe().then((res) => {
            setUserData({ ...res.data });
            
            toast.success("OTP verified successfully");

            // add timeout before calling this route
            setTimeout(() => {
              router.push({
                pathname: routes.home.route,
                query: {
                  onboarding: true
                }
              });
            });
            
            return true;
          })
          .catch((err) => {
            // Sentry.captureException(err);
            isErrRes = true;

            if (err.response.status === 401) {
              fetch(`${QOOHOO_WEBSITE_DOMAIN}/api/auth/logout`).finally(() => {
                toast.error("Some error occurred, please login again.");

                setUserData({});
              });
            }
          });
      })
      .catch((err) => {
        console.log(err);

        if (err.response.status !== 400) {
          // Sentry.captureException(err);
        }

        const baseError = "Error verifying OTP! Please try again."
        const error = err.response.data?.data?.[0] || baseError;

        isErrRes = true;

        toast.error(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  function fetchUserIpInfo() {
    if (!userIpInfo) {
      getIpInfo({ setUserIpInfo });
    }
  }

  function updateDefaultCountryWithIpInfo() {
    if (userIpInfo) {
      const _defaultValue = countries.find(
        (country) => country.code === userIpInfo?.country
      );
      setSelectedCountry(_defaultValue);
    }
  }

  // fetch local country code and set its value
  useEffect(() => {
    fetchUserIpInfo();
  }, []);

  // if local country code updates, then set the default country value
  useEffect(() => {
    updateDefaultCountryWithIpInfo();
  }, [userIpInfo]);

  const currentInput = !isOTPScreen ? (
    <>
      <PhoneInput
        phoneNumber={phoneNumber}
        initialValue={phoneNumber}
        onSubmit={handleGetOtp}
        handlePhoneNumberBlur={handlePhoneNumberBlur}
        handlePhoneNumberChange={handlePhoneNumberChange}
        selectedCountry={selectedCountry}
        setSelectedCountry={setSelectedCountry}
        error={error}
        placeholder={placeholder}
      />
      <span className="text-xs text-[#767676]">{"Get OTP via:"}</span>
      <div className="flex flex-row w-full gap-4 sm:gap-2">
        <GetOtpBtn
          isLoading={isLoadingWA}
          phoneNumber={phoneNumber}
          disabled={!turnstileToken || isLoadingWA || isLoadingSMS}
          selectedCountryCode={selectedCountryCode}
          title="WhatsApp"
          color={GetOtpBtn.prototype.color.WHATSAPP}
          onClick={() => {
            setShouldSendInWhatsapp(true);
          }}
        />
        <GetOtpBtn
          isLoading={isLoadingSMS}
          phoneNumber={phoneNumber}
          disabled={!turnstileToken || isLoadingWA || isLoadingSMS}
          selectedCountryCode={selectedCountryCode}
          title="SMS"
          color={GetOtpBtn.prototype.color.SMS}
          onClick={() => {
            setShouldSendInWhatsapp(false);
          }}
        />
      </div>
    </>
  ) : (
    <div className="flex flex-col justify-center items-start border-[1px] border-[#D5D8DC] outline-none w-full max-w-[420px] rounded-[4px]">
      <input
        id="single-otp-input"
        className={cls(
          "rounded-sm text-2xl font-semibold leading-9 tracking-[0.75em] bg-transparent selection:bg-transparent border-transparent focus:outline-none px-4 py-2",
          otp.length > 0 && "mr-[-0.75em]",
          "placeholder-[#E2E2E2] text-[#16191B]"
        )}
        type="number"
        placeholder="••••••"
        pattern="\d*"
        value={otp}
        autoFocus={true}
        autoComplete="off"
        disabled={false}
        onChange={handleOtpChange}
      />
    </div>
  );

  return (
    <FieldWrapper error={error} hint={hint} visible={visible}>
      {label && (
        <label htmlFor="input" className="font-semibold text-sm">
          {label}
        </label>
      )}
      {/* Mobile Modal Header */}
      <h3 className="text-sm sm:text-base font-normal">
        {isOTPScreen ? (
          <>
            OTP is sent to you on{" "}
            <button
              className="inline-flex space-x-1 cursor-pointer hover:text-[#22272B]/80 underline items-center ml-[0.1rem]"
              onClick={() => {
                setIsOTPScreen(false);
                setOTP("");
              }}
            >
              <span className="align-middle whitespace-nowrap">
                +{selectedCountryCode}-{phoneNumber}
              </span>
              <img
                className="mb-[1px]"
                src="/assets/icons/edit-block.svg"
                alt="edit icon"
              />
            </button>
          </>
        ) : (
          "Verify your phone number to setup your account"
        )}
      </h3>
      {/* Mobile Modal Body */}
      <div className="relative w-full mt-5">{currentInput}</div>
      <Turnstile 
        userRef={turnstileRef}
        retry="auto"
        refreshExpired="auto"
        size="invisible"
        execution="render"
        sitekey={NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY} 
        onVerify={saveTurnstileToken} 
        onError={onTurnstileError}
      />
      {/* Mobile Modal Footer */}
      {isOTPScreen &&
        (isLoadingSMS || isLoadingWA ? (
          <div className="mt-8 flex items-center justify-center">
            <Spinner color="#605FE7" className="!h-6 !w-6"/>
          </div>
        ) : (
          <ResendOTP resendOtp={resendOtp} />
        ))}
      {!isOTPScreen && (
        <p className="text-[#7B8185] text-xs leading-5 mt-5">
          By continuing you agree to our{" "}
          <a className="font-bold" href="https://qoohoo.in/pages/stdeula" target="_blank">
            Terms
          </a>{" "}
          and{" "}
          <a className="font-bold" href="https://qoohoo.in/pages/privacy" target="_blank">
            Privacy policy
          </a>
          .
        </p>
      )}
    </FieldWrapper>
  );
}
